import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Asterisk } from '@/components/generals/asterisk'
import { ButtonLink } from '@/components/generals/button-link'
import { navLinks, siteConfig } from '@/constants/site'

const mobileLinks = [...navLinks, { label: "Contact", href: '/#contact' }]

export const Header: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        let last = window.scrollY

        const onScroll = (): void => {
            const y = window.scrollY

            setScrolled(y > 24)

            if (Math.abs(y - last) > 6) {
                setHidden(y > last && y > 160)
                last = y
            }
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previous = root.style.overflow
        root.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            root.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => {
        const query = window.matchMedia('(min-width: 768px)')

        const onChange = (event: MediaQueryListEvent): void => {
            if (event.matches) setOpen(false)
        }

        query.addEventListener('change', onChange)

        return () => query.removeEventListener('change', onChange)
    }, [])

    const close = (): void => setOpen(false)

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    hidden && !open && '-translate-y-full'
                )}
            >
                <div
                    className={cn(
                        'border-b transition-[background-color,border-color,color] duration-300',
                        open && 'border-transparent bg-transparent text-background',
                        !open && scrolled && 'border-foreground/10 bg-background/80 backdrop-blur-md',
                        !open && !scrolled && 'border-transparent bg-transparent'
                    )}
                >
                    <div className='page-container flex h-16 items-center justify-between gap-6 md:h-20'>
                        <Link
                            href='/'
                            onClick={close}
                            className='group flex items-center gap-2.5 font-heading text-lg font-semibold tracking-[-0.02em]'
                        >
                            <span className='grid size-8 place-items-center rounded-full bg-brand text-brand-foreground transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:rotate-[120deg]'>
                                <Asterisk className='size-4' />
                            </span>

                            {siteConfig.name}
                        </Link>

                        <nav aria-label="Main" className='hidden md:block'>
                            <ul className='flex items-center gap-1'>
                                {navLinks.map(link => (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            className='rounded-full px-4 py-2 text-[15px] font-medium transition-colors hover:bg-foreground/[0.06]'
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className='flex items-center gap-4'>
                            <p className='hidden items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground lg:flex'>
                                <span className='relative flex size-2'>
                                    <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none' />
                                    <span className='relative inline-flex size-2 rounded-full bg-emerald-500' />
                                </span>

                                {siteConfig.availabilityShort}
                            </p>

                            <ButtonLink href='/#contact' size='sm' className='hidden md:inline-flex'>
                                Let's talk
                            </ButtonLink>

                            <button
                                type='button'
                                onClick={() => setOpen(value => !value)}
                                aria-expanded={open}
                                aria-controls='mobile-menu'
                                aria-label={open ? "Close menu" : "Open menu"}
                                className='grid size-11 place-items-center rounded-full border border-current/20 transition-colors hover:border-current md:hidden'
                            >
                                {open ? <X className='size-5' /> : <Menu className='size-5' />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div
                id='mobile-menu'
                data-surface='ink'
                inert={!open}
                className={cn(
                    'fixed inset-0 z-40 flex flex-col bg-foreground text-background md:hidden',
                    'transition-[clip-path] duration-700 ease-[cubic-bezier(0.76,0,0.24,1)]',
                    open ? '[clip-path:inset(0_0_0_0)]' : '[clip-path:inset(0_0_100%_0)]'
                )}
            >
                <div className='page-container flex flex-1 flex-col overflow-y-auto pb-10 pt-24'>
                    <nav aria-label="Mobile">
                        <ul className='flex flex-col'>
                            {mobileLinks.map((link, index) => (
                                <li key={link.href} className='overflow-hidden border-b border-background/10'>
                                    <Link
                                        href={link.href}
                                        onClick={close}
                                        className={cn(
                                            'flex items-baseline justify-between py-3 font-heading text-[clamp(2.75rem,12vw,4.5rem)] font-semibold leading-none tracking-[-0.04em] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]',
                                            open ? 'translate-y-0' : 'translate-y-full'
                                        )}
                                        style={{ transitionDelay: open ? `${150 + index * 60}ms` : '0ms' }}
                                    >
                                        {link.label}

                                        <span className='font-mono text-xs font-normal tracking-normal text-background/50'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-auto flex flex-col gap-6 pt-12'>
                        <p className='flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-background/60'>
                            <span className='size-2 rounded-full bg-emerald-400' />
                            {siteConfig.availability}
                        </p>

                        <ul className='flex flex-wrap gap-x-6 gap-y-3'>
                            {siteConfig.socials.map(social => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='inline-flex items-center gap-1 text-sm text-background/80 transition-colors hover:text-background'
                                    >
                                        {social.label}
                                        <ArrowUpRight className='size-4' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </>
    )
}
